import { ArrowDown, ArrowUp } from 'lucide-react'
import { useMemo } from 'react'
import { Link, useSearchParams } from 'react-router-dom'

import {
  ActivityBarChart,
  BarDatum
} from '@/components/activity/activity-bar-chart'
import {
  ActivityPanel,
  DeltaText,
  ErrorNote,
  GranularitySwitch,
  KpiTile,
  parseGranularity,
  Pulse
} from '@/components/activity/activity-panel'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '@/components/ui/table'
import { cn } from '@/lib/utils'
import type {
  ActivityGranularity,
  PlatformActivitySort,
  SortOrder
} from '@/models/IActivity'
import { RoutePaths } from '@/routes/routes'
import { useQueryGetPlatformActivity } from '@/services'
import {
  ACTIVITY_FOOTER_TEXT,
  formatNumber,
  formatPercent,
  formatPeriodLong,
  formatPeriodShort,
  formatSeatsHint,
  getSeats,
  GRANULARITY_UNIT,
  isLowActivation
} from '@/utils/activity'

const SORTS: PlatformActivitySort[] = ['mau', 'wau', 'activation_rate']
const ORG_LIMIT = 100

export const orgActivityLink = (orgId: string) =>
  `${RoutePaths.ORGANIZATIONS}/${orgId}?tab=activity`

const SortHead = ({
  label,
  sortKey,
  sort,
  order,
  onSort,
  className
}: {
  label: string
  sortKey: PlatformActivitySort
  sort: PlatformActivitySort
  order: SortOrder
  onSort: (sort: PlatformActivitySort) => void
  className?: string
}) => {
  const active = sort === sortKey

  return (
    <TableHead
      aria-sort={
        active ? (order === 'asc' ? 'ascending' : 'descending') : 'none'
      }
      className={className}
    >
      <button
        className={cn(
          'inline-flex items-center gap-1 hover:text-foreground',
          active && 'text-foreground'
        )}
        onClick={() => onSort(sortKey)}
        type="button"
      >
        {label}
        {active &&
          (order === 'asc' ? (
            <ArrowUp className="h-3.5 w-3.5" />
          ) : (
            <ArrowDown className="h-3.5 w-3.5" />
          ))}
      </button>
    </TableHead>
  )
}

export const ActivationChip = ({ rate }: { rate: number | null }) => (
  <span
    className={cn(
      'inline-flex rounded px-1.5 py-0.5 text-sm font-medium tabular-nums',
      isLowActivation(rate)
        ? 'bg-amber-500/15 text-amber-400'
        : 'text-foreground'
    )}
    title={isLowActivation(rate) ? 'Below 50% of seats active' : undefined}
  >
    {formatPercent(rate)}
  </span>
)

const PlatformActivityPage = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const granularity = parseGranularity(searchParams.get('granularity'))
  const sortParam = searchParams.get('sort') as PlatformActivitySort | null
  const sort: PlatformActivitySort =
    sortParam && SORTS.includes(sortParam) ? sortParam : 'mau'
  const order: SortOrder = searchParams.get('order') === 'asc' ? 'asc' : 'desc'

  const update = (patch: Record<string, string>) =>
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev)

      Object.entries(patch).forEach(([key, value]) => next.set(key, value))

      return next
    })

  const { data, isLoading, isError, isFetching } = useQueryGetPlatformActivity(
    { granularity, sort, order, org_limit: ORG_LIMIT },
    { placeholderData: (prev) => prev }
  )

  const summary = data?.summary
  // placeholder data may still be for the previous granularity
  const seriesGranularity: ActivityGranularity =
    data?.granularity ?? granularity

  const [usersData, orgsData] = useMemo(() => {
    const series = data?.series ?? []
    const toDatum = (value: number, periodStart: string, unit: string) => ({
      key: periodStart,
      value,
      label: formatPeriodShort(periodStart, seriesGranularity),
      title: `${formatPeriodLong(
        periodStart,
        seriesGranularity
      )}: ${formatNumber(value)} ${unit}`
    })

    return [
      series.map<BarDatum>((p) =>
        toDatum(p.active_users, p.period_start, 'active users')
      ),
      series.map<BarDatum>((p) =>
        toDatum(p.active_orgs, p.period_start, 'active organizations')
      )
    ]
  }, [data, seriesGranularity])

  const handleSort = (next: PlatformActivitySort) =>
    update(
      next === sort
        ? { sort, order: order === 'desc' ? 'asc' : 'desc' }
        : { sort: next, order: 'desc' }
    )

  return (
    <section
      aria-label="Platform activity"
      className="space-y-4 rounded-xl bg-muted/20 p-6 text-foreground"
    >
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div className="space-y-0.5">
          <h1 className="text-xl font-semibold">Platform activity</h1>
          <p className="text-sm text-muted-foreground/70">
            360 user activity across all organizations
          </p>
        </div>
        <GranularitySwitch
          onChange={(value) => update({ granularity: value })}
          value={granularity}
        />
      </header>

      {isError && <ErrorNote>Platform activity could not be loaded.</ErrorNote>}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5">
        <KpiTile
          hint={
            summary && (
              <DeltaText
                current={summary.dau}
                previous={summary.dau_prev}
                suffix="vs yesterday"
              />
            )
          }
          isLoading={isLoading}
          label="DAU"
          value={formatNumber(summary?.dau)}
        />
        <KpiTile
          hint={
            summary && (
              <DeltaText
                current={summary.wau}
                previous={summary.wau_prev}
                suffix="vs previous 7 days"
              />
            )
          }
          isLoading={isLoading}
          label="WAU"
          value={formatNumber(summary?.wau)}
        />
        <KpiTile
          hint={
            summary && (
              <DeltaText
                current={summary.mau}
                previous={summary.mau_prev}
                suffix="vs previous 30 days"
              />
            )
          }
          isLoading={isLoading}
          label="MAU"
          value={formatNumber(summary?.mau)}
        />
        <KpiTile
          hint="360 open in the last 15 min"
          indicator="live"
          isLoading={isLoading}
          label="Active now"
          value={formatNumber(summary?.active_now)}
        />
        <KpiTile
          hint="with at least one active user"
          isLoading={isLoading}
          label="Active orgs · 30d"
          value={formatNumber(summary?.active_orgs_30d)}
        />
      </div>

      <div
        className={cn(
          'grid grid-cols-1 gap-4 xl:grid-cols-2 transition-opacity',
          isFetching && !isLoading && 'opacity-70'
        )}
      >
        <ActivityPanel
          subtitle={`Distinct users active per ${GRANULARITY_UNIT[seriesGranularity]}`}
          title="Active users"
        >
          {isLoading ? (
            <Pulse className="h-[200px] w-full" />
          ) : (
            <ActivityBarChart
              ariaLabel="Active users over time"
              data={usersData}
              height={200}
            />
          )}
        </ActivityPanel>
        <ActivityPanel
          subtitle={`Organizations with at least one active user per ${GRANULARITY_UNIT[seriesGranularity]}`}
          title="Active organizations"
        >
          {isLoading ? (
            <Pulse className="h-[200px] w-full" />
          ) : (
            <ActivityBarChart
              ariaLabel="Active organizations over time"
              barClassName="fill-emerald-500/60 hover:fill-emerald-500/80"
              data={orgsData}
              height={200}
            />
          )}
        </ActivityPanel>
      </div>

      <ActivityPanel
        subtitle={`Top ${ORG_LIMIT} by ${
          sort === 'activation_rate' ? 'seat activation' : sort.toUpperCase()
        } · activation = MAU / seats (max of included and billable), below 50% highlighted`}
        title="Organizations"
      >
        <Table>
          <TableHeader>
            <TableRow className="border-border/30 hover:bg-transparent">
              <TableHead>Organization</TableHead>
              <SortHead
                label="MAU"
                onSort={handleSort}
                order={order}
                sort={sort}
                sortKey="mau"
              />
              <SortHead
                label="WAU"
                onSort={handleSort}
                order={order}
                sort={sort}
                sortKey="wau"
              />
              <TableHead>Members</TableHead>
              <TableHead>Seats</TableHead>
              <SortHead
                label="Activation"
                onSort={handleSort}
                order={order}
                sort={sort}
                sortKey="activation_rate"
              />
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading
              ? Array.from({ length: 6 }, (_, i) => (
                  <TableRow className="border-border/30" key={i}>
                    <TableCell colSpan={6}>
                      <Pulse className="h-4 w-full" />
                    </TableCell>
                  </TableRow>
                ))
              : (data?.orgs ?? []).map((org) => (
                  <TableRow
                    className="border-border/30 hover:bg-muted/30"
                    key={org.org_id}
                  >
                    <TableCell>
                      <Link
                        className="font-medium underline-offset-4 hover:underline"
                        to={orgActivityLink(org.org_id)}
                      >
                        {org.name || org.org_id}
                      </Link>
                      <div className="font-mono text-xs text-muted-foreground/60">
                        {org.org_id}
                      </div>
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatNumber(org.mau)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatNumber(org.wau)}
                    </TableCell>
                    <TableCell className="tabular-nums">
                      {formatNumber(org.members)}
                    </TableCell>
                    <TableCell
                      className="tabular-nums"
                      title={formatSeatsHint(org)}
                    >
                      {formatNumber(getSeats(org))}
                    </TableCell>
                    <TableCell>
                      <ActivationChip rate={org.activation_rate_30d} />
                    </TableCell>
                  </TableRow>
                ))}
          </TableBody>
        </Table>
      </ActivityPanel>

      <footer className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground/70">
        <span>{ACTIVITY_FOOTER_TEXT}</span>
        {data?.generated_at && (
          <span className="tabular-nums">
            Snapshot {new Date(data.generated_at).toLocaleString()}
          </span>
        )}
      </footer>
    </section>
  )
}

export default PlatformActivityPage
